import React, { useState, useEffect } from 'react';
import { Alert, Typography, TextField, Button, Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../../firebase/firebase';
import ErrorDialog from '../../components/global/ErrorDialog';
import { useOptionalTenant } from '../../tenancy/TenantContext';
import LoadingSpinner from '../../components/global/LoadingSpinner';
import { getHomepageRuntimeConfig } from '../../sitePublishing/publicConfigSource';
import { hasSelectedTenantClaimConflict, resolveAdminLandingPath } from '../../auth/adminAccess';
import { createControlPlaneTenantUrl, createControlPlaneUrl } from '../../tenancy/tenantUrls';
import { getMyTenantAccess, getMyTenantOnboardingStatus } from '../../sitePublishing/api';
import AuthShell, { useBusinessLogoUrl } from '../../components/auth/AuthShell';

const CLOSED_ACCOUNT_PREFIX = 'Account closed: ';

// A closed business keeps its logins until the purge date. Tell its people
// plainly instead of letting them into an empty, locked dashboard.
const closedAccountError = async (): Promise<Error | null> => {
  try {
    const access = await getMyTenantAccess();
    if (!access.closed) return null;
    const deletionDate = new Date(access.purgeAfter).toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric',
    });
    return new Error(`${CLOSED_ACCOUNT_PREFIX}This business account has been closed, so its website and ` +
      `admin access are no longer available. The account and its data will be permanently deleted on ` +
      `${deletionDate}. If you think this is a mistake, contact support@scheduify.com.`);
  } catch {
    return null;
  }
};

const getLoginErrorMessage = (error: unknown): string => {
  const code = typeof error === 'object' && error !== null && 'code' in error
    ? String(error.code)
    : '';
  if (
    code.includes('invalid-credential') ||
    code.includes('invalid-login-credentials') ||
    code.includes('user-not-found') ||
    code.includes('wrong-password')
  ) {
    return 'The email or password does not match an account. If you just signed up, use the exact email address shown during setup.';
  }
  if (code.includes('too-many-requests')) {
    return 'Too many sign-in attempts. Wait a moment and try again.';
  }
  if (error instanceof Error && error.message.startsWith(CLOSED_ACCOUNT_PREFIX)) {
    return error.message.slice(CLOSED_ACCOUNT_PREFIX.length);
  }
  if (error instanceof Error && error.message.startsWith('Unauthorized access:')) {
    return error.message;
  }
  return 'Login failed. Please try again.';
};

const AdminLogin: React.FC = () => {
  const tenantId = useOptionalTenant()?.tenantId;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { user, loading: loadingAuth } = useAuth();
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [confirmResetEmail, setConfirmResetEmail] = useState('');

  const location = useLocation();

  const businessConfigQuery = useQuery({
    queryKey: ['publicBusinessConfig', tenantId],
    queryFn: () => getHomepageRuntimeConfig(tenantId!),
    enabled: Boolean(tenantId),
    retry: false,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });
  const businessName = businessConfigQuery.data?.businessInfo?.name?.trim() || '';
  const logoUrl = useBusinessLogoUrl(businessConfigQuery.data?.businessInfo?.logoImagePath);

  useEffect(() => {
    if (!user || location.pathname !== '/admin') {
      return;
    }

    let active = true;
    user.getIdTokenResult()
      .then(async (idTokenResult) => {
        const isSuperAdmin = idTokenResult.claims.superAdmin === true;
        const claimedTenantId = typeof idTokenResult.claims.websiteId === 'string'
          ? idTokenResult.claims.websiteId : undefined;
        if (hasSelectedTenantClaimConflict(idTokenResult.claims, tenantId)) {
          await signOut(auth);
          if (active) {
            setError('Unauthorized access: Incorrect website.');
            setDialogOpen(true);
          }
          return;
        }
        if (!isSuperAdmin) {
          const closed = await closedAccountError();
          if (closed) {
            await signOut(auth);
            if (active) {
              setError(getLoginErrorMessage(closed));
              setDialogOpen(true);
            }
            return;
          }
        }
        const resolvedTenantId = tenantId ?? claimedTenantId;
        const resolvedLandingPath = resolveAdminLandingPath(idTokenResult.claims, resolvedTenantId);
        if (!resolvedLandingPath) return;
        if (!resolvedTenantId) {
          if (active) window.location.replace(createControlPlaneUrl(resolvedLandingPath));
          return;
        }
        let destination: string = resolvedLandingPath;
        if (!isSuperAdmin) {
          try {
            const onboarding = await getMyTenantOnboardingStatus(resolvedTenantId);
            if (onboarding.lifecycleState === 'onboarding') destination = '/admin/onboarding';
          } catch {
            // Existing tenants without self-service state use their normal landing page.
          }
        }
        if (active) window.location.replace(createControlPlaneTenantUrl(destination, resolvedTenantId));
      })
      .catch((authError) => {
        console.error('Failed to resolve admin access:', authError);
      });

    return () => {
      active = false;
    };
  }, [user, location.pathname, tenantId]);

  const mutation = useMutation({
    mutationFn: async () => {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);
      const user = userCredential.user;

      // Retrieve the ID token result to get custom claims
      const idTokenResult = await user.getIdTokenResult();
      const isSuperAdmin = idTokenResult.claims.superAdmin === true;
      const claimedTenantId = typeof idTokenResult.claims.websiteId === 'string'
        ? idTokenResult.claims.websiteId : undefined;
      if (hasSelectedTenantClaimConflict(idTokenResult.claims, tenantId)) {
        await signOut(auth);
        throw new Error('Unauthorized access: Incorrect website.');
      }
      if (!isSuperAdmin) {
        const closed = await closedAccountError();
        if (closed) {
          await signOut(auth);
          throw closed;
        }
      }
      const resolvedTenantId = tenantId ?? claimedTenantId;
      const resolvedLandingPath = resolveAdminLandingPath(idTokenResult.claims, resolvedTenantId);
      if (!resolvedLandingPath) {
        await signOut(auth);
        throw new Error('Unauthorized access: Incorrect website.');
      }
      if (!resolvedTenantId) {
        return {
          userCredential,
          landingPath: createControlPlaneUrl(resolvedLandingPath),
        };
      }
      let destination: string = resolvedLandingPath;
      if (!isSuperAdmin) {
        try {
          const onboarding = await getMyTenantOnboardingStatus(resolvedTenantId);
          if (onboarding.lifecycleState === 'onboarding') destination = '/admin/onboarding';
        } catch {
          // Existing non-self-service tenants do not have onboarding state.
        }
      }
      const landingPath = createControlPlaneTenantUrl(destination, resolvedTenantId);

      if (!landingPath) {
        await signOut(auth);
        throw new Error('Unauthorized access: Incorrect website.');
      }

      return { userCredential, landingPath };
    },
    onSuccess: ({ landingPath }) => window.location.assign(landingPath),
    onError: (error) => {
      console.error('Login error:', error);
      setError(getLoginErrorMessage(error));
      setDialogOpen(true);
    },
  });

  const handleForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalizedEmail = resetEmail.trim().toLowerCase();
    if (!normalizedEmail || normalizedEmail !== confirmResetEmail.trim().toLowerCase()) {
      setError('Email addresses must match.');
      setDialogOpen(true);
      return;
    }

    try {
      await sendPasswordResetEmail(auth, normalizedEmail, {
        url: tenantId
          ? createControlPlaneTenantUrl('/reset-password', tenantId)
          : createControlPlaneUrl('/reset-password'),
      });


      setResetEmailSent(true);
    } catch (err) {
      console.error('Password reset error:', err);
      setError('Failed to send password reset email. Please try again.');
      setDialogOpen(true);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const isLoading = loadingAuth;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <AuthShell
      logoUrl={logoUrl}
      businessName={businessName}
      title={resetMode ? 'Reset your password' : 'Welcome back'}
      subtitle={resetMode ? 'Enter your account email twice to receive a secure reset link.' : 'Sign in to your business portal.'}
    >
      {resetMode ? <Box component="form" onSubmit={handleForgotPassword} sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}>
        <TextField label="Email" type="email" autoComplete="email" required value={resetEmail}
          onChange={(event) => setResetEmail(event.target.value)} />
        <TextField label="Confirm email" type="email" autoComplete="email" required value={confirmResetEmail}
          onChange={(event) => setConfirmResetEmail(event.target.value)} />
        {resetEmailSent ? <Alert severity="success">Password reset email sent.</Alert> : null}
        <Button variant="contained" type="submit" sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}>Send reset link</Button>
        <Button onClick={() => { setResetMode(false); setResetEmailSent(false); }}>Cancel</Button>
      </Box> : <Box component="form" onSubmit={handleLogin} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <TextField
          label="Email"
          variant="outlined"
          fullWidth
          sx={{ mb: 2 }}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          variant="outlined"
          fullWidth
          sx={{ mb: 3 }}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Typography
          variant="body2"
          sx={{ mb: 2, cursor: 'pointer', color: 'primary.main' }}
          onClick={() => { setResetEmail(email); setConfirmResetEmail(''); setResetMode(true); }}
        >
          Forgot Password?
        </Typography>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          disabled={mutation.isPending}
          fullWidth
          size="large"
          sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
        >
          {mutation.isPending ? 'Signing in...' : 'Sign In'}
        </Button>
      </Box>}
      <ErrorDialog open={dialogOpen} onClose={handleCloseDialog} message={error} />
    </AuthShell>
  );
};

export default AdminLogin;
